import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Backdrop,
  Box,
  Button,
  Card,
  DotProgress,
  FormDialog,
  Grid,
  InputField,
  MuiBox,
  SelectDropdown,
  Switch,
  Typography,
  color
} from '@kis-technologies/kis-ui';
import { ValidPasswordToolTip } from 'components/ValidPasswordToolTip';
import { useActions } from 'hooks/useActions';
import { actions as adminsActions, selectors as adminsSelectors, UpdateAdminPayload } from 'store/admins';
import { selectors as authSelectors } from 'store/auth';
import { selectors as venuesSelectors, actions as venuesActions, Venue } from 'store/venues';
import { getSortedList } from 'utils/stringFormat';
import { MAINTENANCE, INVALID, VALID } from '../constants';
import { useStyles } from '../styled';
import { EditUserOverlayProps, RoleItem } from '../types';
import {
  getHasInitialInput,
  getHasInvalidInput,
  getHasEmptyEditInput,
  getInitialEditForm,
  validationSchema,
  defaultRoles,
  globalRole,
  fullRoleList
} from '../utils';

export const EditUserOverlay = (props: EditUserOverlayProps) => {
  const { onCancel, onSuccess, admin } = props;

  // ACTION CREATORS AND SELECTORS
  const [updateAdmin, getValidPinCode, resetPinCode] = useActions([
    adminsActions.updateAdmin,
    adminsActions.getValidPinCode,
    adminsActions.resetPinCode
  ]);
  const { successful, pending } = useSelector(adminsSelectors.selectAction);
  const validPincode = useSelector(adminsSelectors.selectPinCode);
  const authAdmin = useSelector(authSelectors.selectAdmin);
  const getVenues = useActions(venuesActions.getVenues);
  const venues = useSelector(venuesSelectors.selectVenues);

  const classes = useStyles();

  // SETS THE FORM VALUES AND VALIDATION VALUES FOR EACH FORM VALUE (INVALID | VALID)
  const initialForm = getInitialEditForm(admin);
  const [form, setForm] = useState(initialForm);
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});
  const [showPasswordInfo, setPasswordInfoVisibility] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<Venue>(venues[0]);
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [selectedRole, setSelectedRole] = useState<RoleItem | null>(
    fullRoleList.find((r) => r.code === admin.role) || null
  );

  // CHECKS INVALIDATIONS AND EMPTY INPUTS AND INITIAL VALUES
  const hasEmptyInput = useMemo(() => getHasEmptyEditInput(form, admin), [form, admin]);
  const hasInitialInput = useMemo(() => getHasInitialInput(form, admin), [form, admin]);
  const hasInvalidInput = useMemo(() => getHasInvalidInput(validation), [validation]);

  useEffect(() => {
    if (successful) onSuccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successful]);

  useEffect(() => {
    if (!venues || !venues.length) {
      getVenues();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (venues && venues.length) {
      if (admin.venueId) {
        const venueIndex = venues.findIndex((venue) => venue.id === admin.venueId);
        setSelectedVenue(venues[venueIndex]);
      } else {
        setSelectedVenue(venues[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venues]);

  useEffect(() => {
    let tempRoles: RoleItem[] = [];
    if (authAdmin && authAdmin.role === 'GlobalAdmin') {
      tempRoles = globalRole;
    }
    tempRoles = tempRoles.concat(defaultRoles);
    setRoles(tempRoles);
  }, [authAdmin]);

  useEffect(() => {
    if (validPincode) {
      setForm({ ...form, pinCode: validPincode });
    }

    return () => resetPinCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validPincode]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  // ON CLICK OF SEND ALERT EMAILS SWITCH
  const onAlertEmailsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // SETS FORM MAINTENANCE OR CLIENT ADMIN CHECKBOXES
    setForm({ ...form, sendAlertEmails: !form.sendAlertEmails });
  };

  const handleRoleChange = (role: RoleItem) => {
    setSelectedRole(role);
    setForm({ ...form, role: role.code });
  };

  // FORM INPUT CHANGE
  const onInputChange = (key: keyof ReturnType<typeof getInitialEditForm>, e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();

    // IF THERE WAS VALIDATION ERROR FOR SELECTED INPUT - RESET IT TO VALID
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }
    // UPDATE THE FORM OBJECT WITH THE CHANGE TO CORRESPONDING KEY VALUE PAIR
    setForm({ ...form, [key]: e.target.value });

    if (key === 'password') {
      setPasswordInfoVisibility(true);
    }
  };

  // FORM INPUT BLUR
  const onInputBlur = async (
    key: keyof ReturnType<typeof getInitialEditForm>,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    // TRIMS WHITE SPACE OF THE INPUT VALUES ONBLUR
    setForm({ ...form, [key]: e.target.value.trim() });
    let invalidMessage = null;

    // IF THE TRIMMED STRING VALUE HAS VALUE RUN VALIDATION AND SET INVALID MESSAGE
    if (value && value.toString().trim().length) {
      const result = await validationSchema.validate({ [key]: value && value.toString().trim() }).catch((err) => err);
      invalidMessage = result && result.message;
    }

    // SET VALIDATION TO BE VALID OR INVALID DEPENDING ON WHETHER INVALID MESSAGE EXISTS
    setValidation({ ...validation, [key]: invalidMessage ? INVALID : VALID });
    setValidationMessage({ ...validationMessage, [key]: invalidMessage });

    if (key === 'password') {
      setPasswordInfoVisibility(false);
    }
  };

  const onVenueChange = (item: Venue) => {
    setSelectedVenue(item);
    setForm({ ...form, venueId: item.id });
  };

  // FORM INPUT VALIDATION
  const validate = (key: keyof ReturnType<typeof getInitialEditForm>) => {
    return validationMessage[key];
  };

  // FORM SUBMIT
  const onUpdateUserClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    // SUBMITS THE FORM TO UPDATE ADMIN
    const payload: UpdateAdminPayload = {
      id: admin.id,
      name: form.name,
      role: form.role,
      sendAlertEmails: form.sendAlertEmails,
      venueId: form.role === MAINTENANCE && selectedVenue ? selectedVenue.id : undefined,
      password: form.password ? form.password : undefined,
      email: form.email,
      pinCode: form.pinCode
    };

    updateAdmin(payload);
  };

  // CHECKS ROLE
  const isMaintenance = form.role === MAINTENANCE;

  // CONDITIONS FOR DISABLING SUBMIT BUTTON
  const buttonDisabled = hasEmptyInput || hasInvalidInput || hasInitialInput;

  return (
    <>
      {pending && (
        <Backdrop open={pending ? true : false}>
          <DotProgress color={color.GRAY} />
        </Backdrop>
      )}
      <FormDialog
        open
        title="Edit User"
        cancelButtonAction={onCancel}
        cancelButtonText="Cancel"
        confirmButtonAction={onUpdateUserClick}
        confirmButtonDisabled={buttonDisabled}
        confirmButtonText="Save"
      >
        <Grid className={classes.formInnerContainer}>
          {/* DROPDOWN - ROLE */}
          <MuiBox className={classes.formDropdownBox}>
            <Typography className={classes.formDropdownLabel}>Role</Typography>
            <SelectDropdown
              placeholder="Select a Role"
              items={getSortedList(roles, 'name')}
              selectedItem={selectedRole as RoleItem}
              onChange={(item: RoleItem) => handleRoleChange(item)}
              itemToString={(item: RoleItem) => (item ? item.name : '')}
              radius="round"
              width="262px"
              sx={{
                boxShadow: 'none',
                height: '45px',
                '& .MuiSelect-select': {
                  color: selectedRole ? '#000' : '#9E9E9E'
                }
              }}
            />
          </MuiBox>
          {/* INPUT - NAME */}
          <MuiBox className={classes.formInputBox}>
            <InputField
              label="Name"
              size="small"
              fullWidth
              value={form.name}
              onChange={(e) => onInputChange('name', e)}
              onBlur={(e) => onInputBlur('name', e, e.target.value.trim())}
              helperText={validate('name')}
              error={validation.name === INVALID}
              sx={{ width: '262px' }}
            />
          </MuiBox>
          {/* INPUT - EMAIL */}
          <MuiBox className={classes.formInputBox}>
            <InputField
              label="Email Address"
              size="small"
              fullWidth
              value={form.email}
              onChange={(e) => onInputChange('email', e)}
              onBlur={(e) => onInputBlur('email', e, e.target.value.trim())}
              helperText={validate('email')}
              error={validation.email === INVALID}
              sx={{ width: { xs: '262px', sm: '524px' } }}
            />
          </MuiBox>
          <Grid className={classes.formPinBlock}>
            {/* INPUT - PIN CODE */}
            <MuiBox className={classes.formPinInputBox}>
              <InputField
                label="PIN Code"
                size="small"
                fullWidth
                value={form.pinCode}
                onChange={(e) => onInputChange('pinCode', e)}
                onBlur={(e) => onInputBlur('pinCode', e, e.target.value.trim())}
                helperText={validate('pinCode')}
                error={validation.pinCode === INVALID}
                sx={{ width: { xs: '262px', sm: '175px' } }}
              />
            </MuiBox>
            {/* BUTTON - GENERATE NEW PIN */}
            <MuiBox className={classes.formButtonBox}>
              <Button
                customSize="small"
                onClick={getValidPinCode}
                width={200}
                type="submit"
                className={classes.formButton}
              >
                Generate New PIN
              </Button>
            </MuiBox>
          </Grid>
          {/* INPUT - PASSWORD */}
          {!isMaintenance && admin.role === 'MaintenanceAdmin' && (
            <MuiBox className={classes.formInputBox}>
              <InputField
                label="Password"
                size="small"
                fullWidth
                value={form.password}
                type="text"
                onChange={(e) => onInputChange('password', e)}
                onBlur={(e) => onInputBlur('password', e, e.target.value.trim())}
                onFocus={() => setPasswordInfoVisibility(true)}
                helperText={validate('password')}
                error={validation.password === INVALID}
                endIcon={<ValidPasswordToolTip showValidPasswordInfo={showPasswordInfo} value={form.password} />}
                sx={{ width: '262px' }}
                inputProps={{ sx: { WebkitTextSecurity: 'disc' } }}
              />
            </MuiBox>
          )}
          {/* DROPDOWN - VENUE */}
          {isMaintenance && (
            <MuiBox className={classes.formDropdownBox}>
              <Typography className={classes.formDropdownLabel}>Venue Name</Typography>
              <SelectDropdown
                placeholder="Select a Venue"
                items={getSortedList(venues, 'name')}
                selectedItem={selectedVenue}
                onChange={(item: Venue) => onVenueChange(item)}
                itemToString={(item: Venue) => (item ? item.name : '')}
                radius="round"
                width="262px"
                sx={{
                  boxShadow: 'none',
                  height: '45px',
                  '& .MuiSelect-select': {
                    color: selectedVenue ? '#000' : '#9E9E9E'
                  }
                }}
              />
            </MuiBox>
          )}
          {/* SWITCH - SEND EMAILS */}
          <Box flexOverride="none" fullWidth>
            <Card>
              <Card.ActionArea onClick={onAlertEmailsClick}>
                <Grid className={classes.formSwitchBlock}>
                  <Switch
                    checked={form.sendAlertEmails}
                    checkedLabel="Send alert emails"
                    uncheckedLabel="Send alert emails"
                    labelSx={{ fontWeight: 600 }}
                  />
                </Grid>
              </Card.ActionArea>
            </Card>
          </Box>
        </Grid>
      </FormDialog>
    </>
  );
};
